import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { academyApi } from "@/lib/academy";
import { BELT_COLORS, BELT_INK } from "@/lib/academy";
import AcademySuggest from "@/components/AcademySuggest";

export default function AcademyPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    academyApi.overview().then(setData).catch(() => setData({ belts: [], lessons: [], labs: [], stats: {} }));
  }, []);

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: "Solar Black Belt Academy",
    description: "Free, self-paced commercial solar and Tesla Powerwall training — 24 lessons across 8 belt levels (White through Black), server-graded assessments, human-graded written tasks and practical labs, an internal Solar Black Belt credential and the Powerwall Product Knowledge Certificate. Issued by Coastal Energy; completion enables hiring consideration, never an automatic job offer.",
    provider: { "@type": "Organization", name: "Commercial Solar ROI — Coastal Energy", url: "https://commercialsolarroi.com" },
    educationalCredentialAwarded: ["Commercial Solar ROI — Solar Black Belt (White–Black Belt)", "Powerwall Product Knowledge Certificate"],
    offers: { "@type": "Offer", price: 0, priceCurrency: "USD", category: "Free during program setup" },
    inLanguage: "en",
    hasCourseInstance: { "@type": "CourseInstance", courseMode: "online", courseWorkload: "PT15H" },
  };

  return (
    <>
      <Seo
        title="Solar Black Belt Academy | Coastal Energy — Train on Solar & Tesla Powerwall"
        description="Free, self-paced solar and Tesla Powerwall classes with 8 belt levels, graded assessments, labs and an internal certification from Coastal Energy. Train in Florida and get considered for solar jobs. Enroll at the Solar Black Belt Academy."
      />
      <script type="application/ld+json">{JSON.stringify(courseSchema)}</script>
      <section className="academy-hero" data-testid="academy-hero">
        <div className="wrap academy-hero-grid">
          <div>
            <span className="eyebrow">TRAIN WITH THE SENSEI</span>
            <h1 data-testid="academy-heading">The Solar Black Belt Academy</h1>
            <p className="lead">
              Know solar the way a sensei knows the dojo. 24 concise lessons across 8 belt levels — panels,
              inverters, safety, commercial bills, Tesla Powerwall, money math and the Black Belt capstone.
              Self-paced, free during program setup, graded server-side.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 20 }}>
              <Link className="btn lime" data-testid="academy-enroll-cta" to="/academy/register">Sign up — start your White Belt ↗</Link>
              <Link className="btn light" data-testid="academy-inside-link" to="/academy/inside">See what's inside the class</Link>
              <a className="btn light" href="/academy-assets/solar-black-belt-academy-syllabus.pdf" target="_blank" rel="noopener noreferrer" data-testid="academy-syllabus-download">Download the syllabus (PDF)</a>
            </div>
            <p className="micro" style={{ marginTop: 14 }}>
              {data?.stats ? `${data.stats.lessons} lessons · ${data.stats.questions} practice & exam questions · ${data.stats.terms} vocabulary terms · ${data.stats.labs} practical labs · about ${Math.round(data.stats.totalMinutes / 60)} hours total` : "Loading curriculum…"}
            </p>
          </div>
          <img className="academy-hero-ninja" src="/academy-assets/ninja-stash.jpg" alt="Roi Solar ninja with solar-glasses sitting on his training rewards" data-testid="academy-hero-ninja" />
        </div>
      </section>

      <section className="wrap academy-section" data-testid="academy-belt-path">
        <span className="eyebrow">THE BELT PATH</span>
        <h2>Eight belts. One honest education.</h2>
        <p className="lead" style={{ maxWidth: 760 }}>
          Every belt pairs three lessons with a graded assessment, a written task and a practical lab. Pass the
          belt exam — 80% or higher with every safety-critical item correct — to unlock the next belt.
        </p>
        <div className="academy-belt-grid">
          {(data?.belts || []).map((b) => (
            <Link to={`/academy/belts/${b.id}`} className="academy-belt-card" key={b.id} data-testid={`academy-card-${b.id}`}>
              <img src={b.scene} alt={`${b.name} Belt class at the academy`} loading="lazy" />
              <span className="academy-belt-chip" style={{ background: BELT_COLORS[b.name], color: BELT_INK[b.name] }}>{b.name} Belt</span>
              <p>{b.summary}</p>
              <span className="micro">Exam threshold {b.pass_percent}% · {b.review_required ? "Reviewer sign-off required" : "Self-paced"}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="academy-dark" data-testid="academy-credentials">
        <div className="wrap">
          <span className="eyebrow">WHAT YOU EARN</span>
          <h2>Certification, honestly framed.</h2>
          <div className="academy-cert-grid">
            <div>
              <h3>Solar Black Belt belts</h3>
              <p>Eight internal belt credentials — White through Black — issued by Coastal Energy after server-verified exams, human-graded writing and practical labs. Black Belt adds the warehouse capstone and an oral defense.</p>
            </div>
            <div>
              <h3>Powerwall Product Knowledge Certificate</h3>
              <p>Earned at Purple Belt: every Powerwall lesson and lab, a current manufacturer-source lookup assessment, safety review and reviewer sign-off. Issued by Coastal Energy.</p>
            </div>
            <div>
              <h3>Career consideration</h3>
              <p>Finishing opens the optional hiring pipeline at Coastal Energy — interviews for installer, sales and support roles. Completion is never an automatic job offer or contractor authorization.</p>
            </div>
          </div>
          <p className="micro" style={{ marginTop: 18, maxWidth: 860 }}>
            Honest scope: these are internal company credentials, not a Tesla certification, not NABCEP, not an
            electrician's license and not accredited education. Tesla qualification is issued by Tesla. Every
            certificate carries a 12-month renewal date, an issuer signature and a public verification link.
          </p>
          <Link className="btn lime" data-testid="academy-credentials-cta" to="/academy/register">Get certified — start today ↗</Link>
        </div>
      </section>

      <section className="wrap academy-section" data-testid="academy-links">
        <div className="academy-cert-grid">
          <Link to="/academy/glossary">Glossary — {data?.stats?.terms || 144} solar terms →</Link>
          <Link to="/academy/resources">Official sources & Tesla documentation →</Link>
          <Link to="/academy/capstone">Practical labs & the capstone →</Link>
        </div>
        <AcademySuggest page="/academy" />
      </section>
    </>
  );
}
